// Shared footer — non-sticky, flows with page content
// Appears at bottom of layout; visible without scroll on short pages, requires scroll on long pages
import { useTranslation } from 'react-i18next';
import logoUrl from '../assets/icons/logo-startus.svg';

type FooterVariant = 'web' | 'admin' | 'hub';

interface FooterProps {
  variant?: FooterVariant;
}

export function Footer({ variant = 'web' }: FooterProps) {
  const { t } = useTranslation('common');

  // Legal column is shared across all variants
  const LEGAL_COLUMN = {
    title: t('footer.legal.title'),
    links: [
      { label: t('footer.legal.privacyPolicy'), href: '/privacy-policy', external: true },
      { label: t('footer.legal.termsOfService'), href: '/terms-of-service', external: true },
      { label: t('footer.legal.cookiePolicy'), href: '/cookies', external: true },
    ],
  };

  const WEB_NAV_COLUMNS = [
    {
      title: t('footer.product.title'),
      links: [
        { label: t('footer.product.dashboard'), href: '/app/dashboard', external: false },
        { label: t('footer.product.applications'), href: '/app/applications', external: false },
        { label: t('footer.product.profile'), href: '/app/profile', external: false },
        { label: t('footer.product.startupProfile'), href: '/app/startup', external: false },
      ],
    },
    LEGAL_COLUMN,
  ];

  const ADMIN_NAV_COLUMNS = [
    {
      title: t('footer.admin.title'),
      links: [
        { label: t('footer.admin.dashboard'), href: '/admin/dashboard', external: false },
        { label: t('footer.admin.openCalls'), href: '/admin/open-calls', external: false },
        { label: t('footer.admin.startups'), href: '/admin/startups', external: false },
        { label: t('footer.admin.users'), href: '/admin/users', external: false },
      ],
    },
    LEGAL_COLUMN,
  ];

  const HUB_NAV_COLUMNS = [
    {
      title: t('footer.hub.title'),
      links: [
        { label: t('footer.hub.dashboard'), href: '/hub/dashboard', external: false },
        { label: t('footer.hub.startups'), href: '/hub/inversionista', external: false },
        { label: t('footer.hub.users'), href: '/hub/users', external: false },
      ],
    },
    LEGAL_COLUMN,
  ];

  const NAV_COLUMNS =
    variant === 'admin' ? ADMIN_NAV_COLUMNS : variant === 'hub' ? HUB_NAV_COLUMNS : WEB_NAV_COLUMNS;

  return (
    <footer className="bg-[#1E1E1E] border-t border-white/10 text-white/60">
      {/* Main layout: brand left, nav columns right */}
      <div className="px-8 py-12 flex flex-col gap-10 sm:flex-row sm:items-start sm:justify-between">
        {/* Brand column */}
        <div className="flex flex-col gap-4 max-w-sm">
          <img src={logoUrl} alt="StartUs" className="h-8 w-auto self-start" draggable={false} />
          <p className="text-sm leading-relaxed">{t('footer.tagline')}</p>
        </div>

        {/* Nav columns — grouped to the right */}
        <div className="flex gap-12">
          {NAV_COLUMNS.map((col) => (
            <div key={col.title} className="flex flex-col gap-3">
              <span className="text-xs font-semibold uppercase tracking-widest text-white/40">
                {col.title}
              </span>
              <ul className="flex flex-col gap-2">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      className="text-sm hover:text-white transition-colors"
                      {...(link.external && { target: '_blank', rel: 'noopener noreferrer' })}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="px-8 py-4 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-white/40">
        <span>{t('footer.copyright', { year: new Date().getFullYear() })}</span>
        <span>{t('footer.builtBy')}</span>
      </div>
    </footer>
  );
}
